import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { renderSVG } from 'uqr';
import type { TotpSetup } from '@ste/shared';
import { ApiRequestError, postJson, useAuth, type TokenResponse } from '../../api/auth.ts';
import { Banner, Button, Spinner } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';

export const inputClass =
  'mt-1 block min-h-12 w-full rounded-xl border border-border bg-surface-2 px-4 text-base text-text placeholder:text-muted focus:border-accent focus:outline-none';

/** QR code, app link and manual key for an authenticator app (ADR-0013) */
export function TotpEnrollment({ setup }: { setup: TotpSetup }) {
  // black on white with a quiet zone, readable by every scanner, also in dark mode
  const qr = useMemo(
    () => `data:image/svg+xml;utf8,${encodeURIComponent(renderSVG(setup.uri, { border: 2 }))}`,
    [setup.uri],
  );
  const grouped = setup.secret.match(/.{1,4}/g)?.join(' ') ?? setup.secret;
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">{t('totp.intro')}</p>
      <ol className="list-decimal space-y-2 pl-5 text-sm">
        <li>{t('totp.step1')}</li>
        <li>
          {t('totp.step2')}
          <img
            src={qr}
            alt={t('totp.qrAlt')}
            width={200}
            height={200}
            className="mt-2 rounded-lg bg-white"
            style={{ imageRendering: 'pixelated' }}
          />
          <a className="mt-2 inline-flex min-h-11 items-center text-accent-text underline" href={setup.uri}>
            {t('totp.openApp')}
          </a>
          <details className="text-muted">
            <summary className="min-h-11 cursor-pointer py-2">{t('totp.manual')}</summary>
            <code className="block rounded-lg bg-surface-2 px-3 py-2 font-mono text-base break-all text-text select-all">
              {grouped}
            </code>
          </details>
        </li>
        <li>{t('totp.step3')}</li>
      </ol>
    </div>
  );
}

export function TotpCodeInput({ value, onChange }: { value: string; onChange(value: string): void }) {
  return (
    <label className="block">
      <span className="text-sm text-muted">{t('totp.code')}</span>
      <input
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        className={`${inputClass} tracking-[0.4em]`}
      />
    </label>
  );
}

/**
 * Mandatory TOTP setup after a correct password (STE Connect answered totp_setup_required).
 * The setup token allows nothing else; confirming the first code signs the user in.
 */
export function TotpSetupForm({
  setupToken,
  onCancel,
  onDone,
}: {
  setupToken: string;
  onCancel(): void;
  onDone?(): void;
}) {
  const [setup, setSetup] = useState<TotpSetup>();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    postJson<TotpSetup>('/auth/totp/setup-token', { setupToken }).then(setSetup, (e: unknown) =>
      setError(e instanceof ApiRequestError && e.status === 401 ? t('totp.expired') : t('common.error')),
    );
  }, [setupToken]);

  async function confirm(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const tokens = await postJson<TokenResponse>('/auth/totp/confirm-setup', { setupToken, totp: code });
      useAuth.getState().adopt(tokens);
      onDone?.();
    } catch (e) {
      setError(
        e instanceof ApiRequestError ? (e.status === 401 ? t('totp.expired') : e.message) : t('common.error'),
      );
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={confirm} className="space-y-4" noValidate>
      <h1 className="text-xl font-semibold">{t('totp.title')}</h1>
      {error && <Banner tone="danger" title={error} />}
      {!setup && !error && <Spinner label={t('common.loading')} />}
      {setup && (
        <>
          <TotpEnrollment setup={setup} />
          <TotpCodeInput value={code} onChange={setCode} />
          <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>
            {t('totp.confirm')}
          </Button>
        </>
      )}
      <Button type="button" variant="ghost" className="w-full" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
    </form>
  );
}
